class PageHeaderSection extends HTMLElement {
  constructor() {
    super();

    this.isSticky = this.dataset.sticky === "true";
    this.isStickyAlways = this.dataset.stickyAlways === "true";
    this.isTransparent = this.dataset.transparent === "true";
    this.isAlwaysMobileMenu = this.dataset.alwaysMobileMenu === "true";

    this.classBodyAlwaysMobileMenu = "mobile-nav";

    this.header = document.querySelector(".page-header");
    this.desktopMenuTrigger = document.querySelector(
      ".wt-header__sticky-menu-trigger",
    );
    this.desktopMenuBar = document.querySelector(".wt-drawer--nav");

    this.enabledClass = "sticky-enabled";
    this.showClass = "sticky-show";
    this.desktopHeaderWithMenuBarClass = "page-header--sticky-show-menubar-lg";
  }

  connectedCallback() {
    this.init();

    // Home-only: toggle compact logo class when user scrolls 100px
    this.initLogoScrollClass();

    // Expose --header-height so non-overlay layouts can reserve flow space
    // for the fixed header.
    this.initHeaderHeightVar();
  }

  disconnectedCallback() {
    this.disableStickyHeader();

    // Clean up home scroll listener
    if (this.logoScrollHandler) {
      window.removeEventListener("scroll", this.logoScrollHandler);
      this.logoScrollHandler = null;
    }

    if (this.headerHeightObserver) {
      this.headerHeightObserver.disconnect();
      this.headerHeightObserver = null;
    }
    if (this.headerResizeHandler) {
      window.removeEventListener("resize", this.headerResizeHandler);
      this.headerResizeHandler = null;
    }
  }

  initHeaderHeightVar() {
    const target = this.header || this;

    const update = () => {
      const h = target.getBoundingClientRect().height;
      if (h > 0) {
        document.documentElement.style.setProperty(
          "--header-height",
          `${h}px`,
        );
      }
    };

    // Defer first measurement to ensure layout is complete
    requestAnimationFrame(update);

    this.headerResizeHandler = update;
    window.addEventListener("resize", this.headerResizeHandler, {
      passive: true,
    });

    if (typeof ResizeObserver !== "undefined") {
      this.headerHeightObserver = new ResizeObserver(update);
      this.headerHeightObserver.observe(target);
    }
  }

  enbleStickyHeader() {
    if (!this.header) {
      console.error("Header element not found for enabling sticky header");
      return;
    }

    document.body.classList.add("page-header-sticky");

    let prevScrollpos = window.pageYOffset;

    const isDesktop = window.matchMedia("(min-width: 1200px)").matches;
    const isMenuBarOpen = () =>
      this.header.classList.contains(this.desktopHeaderWithMenuBarClass);
    const isHeaderWithDesktopNav =
      !document.body.classList.contains("mobile-nav");
    const allLLevelsLinks =
      this.desktopMenuBar?.querySelectorAll("a[data-menu-level]");
    const onlyLevel1Links = this.desktopMenuBar?.querySelectorAll(
      "a[data-menu-level='1']",
    );

    const calculateNavbarTopMargin = () => {
      const header = document.querySelector("#header");
      const navbar = document.querySelector("#wt-drawer-nav");
      let marginTop = 0;

      if (navbar.offsetHeight > header.offsetHeight) {
        marginTop = header.offsetHeight - navbar.offsetHeight;
      } else {
        marginTop = Math.abs(navbar.offsetHeight - header.offsetHeight);
      }

      navbar.style.setProperty("--top-margin", `${marginTop}px`);

      return marginTop;
    };

    calculateNavbarTopMargin();

    // Helper kept for sticky mode (non-home pages that might use it)
    const toggleLogoSize = (scrollPos = window.pageYOffset) => {
      const logoImgs = document.querySelectorAll(".wt-header__logo__img");
      const compactClass = "wt-header__logo__img--scrolled";
      const threshold = 100; // px

      logoImgs.forEach((img) => {
        img.classList.toggle(compactClass, scrollPos >= threshold);
      });
    };

    const stickyHeader = {
      show: () => {
        if (this.header) this.header.classList.add(this.showClass);
        stickyHeader.visible = true;
        stickyHeader.handleBehavior();
        calculateNavbarTopMargin();
      },
      hide: () => {
        if (this.header) this.header.classList.remove(this.showClass);
        stickyHeader.visible = false;
        stickyHeader.handleBehavior();
      },
      enable: () => {
        if (this.header) this.header.classList.add(this.enabledClass);
        stickyHeader.enabled = true;
        stickyHeader.handleBehavior();
      },
      disable: () => {
        if (this.header)
          this.header.classList.remove(this.enabledClass, this.showClass);
        stickyHeader.enabled = false;
        stickyHeader.handleBehavior();
      },
      enabled: false,
      visible: true,
      handleBehavior: () => {
        if (isHeaderWithDesktopNav && isDesktop && this.header) {
          stickyHeader.log();
          if (!isMenuBarOpen() && stickyHeader.enabled) {
            setTabindex(allLLevelsLinks, "-1");
          }
          if (isMenuBarOpen() && stickyHeader.enabled) {
            setTabindex(allLLevelsLinks, "0");
          }
          if (!stickyHeader.enabled) {
            setTabindex(onlyLevel1Links, "0");
            if (this.desktopMenuTrigger)
              setTabindex([this.desktopMenuTrigger], "-1");
          } else if (this.desktopMenuTrigger)
            setTabindex([this.desktopMenuTrigger], "0");
        }
      },
      log: () => {},
    };

    // Save the scroll handler for later removal
    this.scrollHandler = () => {
      const currentScrollPos = window.pageYOffset;
      if (!this.isStickyAlways) {
        if (prevScrollpos > currentScrollPos) {
          stickyHeader.show();
        } else {
          stickyHeader.hide();
        }
      } else {
        stickyHeader.show();
      }

      // Update logo size based on scroll distance
      toggleLogoSize(currentScrollPos);

      prevScrollpos = currentScrollPos;
    };

    window.addEventListener("scroll", this.scrollHandler);

    // Ensure correct logo size on init (in case page loads scrolled)
    toggleLogoSize(window.pageYOffset);

    this.desktopMenuTrigger?.addEventListener("click", (e) => {
      e.preventDefault();
      this.desktopMenuBar?.classList.toggle("wt-drawer--nav-show");
      this.desktopMenuTrigger?.classList.toggle(
        "wt-header__sticky-menu-trigger--active",
      );
      this.header?.classList.toggle(this.desktopHeaderWithMenuBarClass);
      stickyHeader.handleBehavior();
    });

    const sentinel = document.querySelector(".sticky-header__threshold");
    const handleStickySentinel = (entries) => {
      entries.forEach(({ isIntersecting }) => {
        if (isIntersecting) {
          stickyHeader.disable();
        } else {
          stickyHeader.enable();
        }
      });
    };

    this.stickyHeaderObserver = new IntersectionObserver(handleStickySentinel, {
      root: null,
      rootMargin: `${this.isStickyAlways ? "-160" : "-100"}px 0px 0px 0px`,
      threshold: 0,
    });
    this.stickyHeaderObserver.observe(sentinel);
  }

  disableStickyHeader() {
    if (this.header) {
      // Remove classes added by sticky header
      this.header.classList.remove(
        this.enabledClass,
        this.showClass,
        this.desktopHeaderWithMenuBarClass,
      );
      document.body.classList.remove("page-header-sticky");
      this.desktopMenuBar?.classList.remove("wt-drawer--nav-show");
      this.desktopMenuTrigger?.classList.remove(
        "wt-header__sticky-menu-trigger--active",
      );
    }

    // Remove the scroll event listener
    if (this.scrollHandler) {
      window.removeEventListener("scroll", this.scrollHandler);
      this.scrollHandler = null;
    }

    // Clean up compact class when disabling sticky behavior
    document
      .querySelectorAll(".wt-header__logo__img")
      .forEach((img) => img.classList.remove("wt-header__logo__img--scrolled"));

    // Disconnect the IntersectionObserver
    if (this.stickyHeaderObserver) {
      this.stickyHeaderObserver.disconnect();
      this.stickyHeaderObserver = null;
    }
  }

  // Independent logic: home template logo shrink on scroll >= 100px
  initLogoScrollClass() {
    const isHome =
      document.body.classList.contains("template-index") ||
      document.body.classList.contains("template-page-home");

    if (!isHome) return;

    const logoImgs = document.querySelectorAll(".wt-header__logo__img");
    if (!logoImgs.length) return;

    const compactClass = "wt-header__logo__img--scrolled";
    const fadeOutClass = "wt-header__logo__img--fade-out";
    const fadeInClass = "wt-header__logo__img--fade-in";
    const threshold = 100;
    const mobileQuery = window.matchMedia("(max-width: 750px)");

    const animateTo = (img, compactWanted) => {
      const isAnimating = img.dataset.animating === "1";
      const hasCompact = img.classList.contains(compactClass);
      if (isAnimating || hasCompact === compactWanted) return;

      img.dataset.animating = "1";
      img.classList.add(fadeOutClass);

      const onFadeOutEnd = (ev) => {
        if (ev.propertyName !== "opacity") return;
        img.removeEventListener("transitionend", onFadeOutEnd);

        img.classList.remove(fadeOutClass);
        img.classList.toggle(compactClass, compactWanted);

        img.classList.add(fadeInClass);
        const onFadeInEnd = (ev2) => {
          if (ev2.propertyName !== "opacity") return;
          img.removeEventListener("transitionend", onFadeInEnd);
          img.classList.remove(fadeInClass);
          delete img.dataset.animating;
        };
        img.addEventListener("transitionend", onFadeInEnd);
      };

      img.addEventListener("transitionend", onFadeOutEnd);
    };

    const update = () => {
      const shouldCompact = window.pageYOffset >= threshold;
      const isMobile = mobileQuery.matches;
      logoImgs.forEach((img) => {
        const isSwapLogo =
          img.classList.contains("wt-header__logo__img--normal") ||
          img.classList.contains("wt-header__logo__img--special");
        if (isMobile && !isSwapLogo) {
          animateTo(img, shouldCompact);
        } else {
          img.classList.toggle(compactClass, shouldCompact);
        }
      });
    };

    // Save and bind listener
    this.logoScrollHandler = () => update();
    window.addEventListener("scroll", this.logoScrollHandler, { passive: true });

    // Re-evaluate on breakpoint changes
    mobileQuery.addEventListener?.("change", update);

    // Initial state (no animation to avoid flicker on load)
    const initialCompact = window.pageYOffset >= threshold;
    logoImgs.forEach((img) => img.classList.toggle(compactClass, initialCompact));
  }

  init() {
    if (this.isSticky) {
      this.enbleStickyHeader();
    } else {
      this.disableStickyHeader();
    }
  }
}

customElements.define("page-header", PageHeaderSection);
